import { T } from '@/components/i18n/message.i18n.component';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs/tabs.component';
import { ProductBuildingBlock } from '@/gql/graphql';
import { cn } from '@/lib/utils';
import { pretailoringPageMessages } from '@/pages/products/pre-tailoring/pre-tailoring.messages';
import React from 'react';
import { DataBlocksTab } from './tabs/data-blocks.tab';
import { DesignObligationTab } from './tabs/design-obligation.tab';
import { GeneralDetailsTab } from './tabs/general-details.tab';
import { InformationTab } from './tabs/information.tab';

type Props = {
  buildingBlock: ProductBuildingBlock;
};

export const BuildingBlockDetailsRow = ({
  className,
  context,
  label,
  value,
  ...props
}: {
  context: string;
  label?: React.ReactNode;
  value: string | React.ReactNode | null | undefined;
} & React.HTMLAttributes<HTMLDivElement>) => {
  const renderValue = (value: string | React.ReactNode | null | undefined) => {
    if (React.isValidElement(value)) return value;
    return (
      <p data-testid={`building-block-details-row-${context}-value`} className="typo-primary-400">
        {value}
      </p>
    );
  };

  return (
    <div className={cn('flex flex-col items-start gap-y-2', className)} {...props}>
      {label && (
        <p data-testid={`building-block-details-row-${context}-label`} className="typo-secondary text-secondary-text">
          {label}
        </p>
      )}
      {renderValue(value)}
    </div>
  );
};

export const BuildingBlockDetails = ({ buildingBlock: bb }: Props) => {
  if (!bb) return null;
  return (
    <Tabs defaultValue="general-details" className="flex flex-col">
      <TabsList className="mb-5" data-testid="building-block-details-tabs-list">
        <TabsTrigger value="general-details" data-testid="building-block-details-tabs-general-details-tab-trigger">
          <p className="typo-primary-500">
            <T {...pretailoringPageMessages.singleBuildingBlockTabGeneralDetails} />
          </p>
        </TabsTrigger>
        <TabsTrigger value="information" data-testid="building-block-details-tabs-information-tab-trigger">
          <p className="typo-primary-500">
            <T {...pretailoringPageMessages.singleBuildingBlockTabInformation} />
          </p>
        </TabsTrigger>
        <TabsTrigger value="data-blocks" data-testid="building-block-details-tabs-data-blocks-tab-trigger">
          <p className="typo-primary-500">
            <T {...pretailoringPageMessages.singleBuildingBlockTabDataBlocks} />
          </p>
        </TabsTrigger>
        <TabsTrigger value="design-obligation" data-testid="building-block-details-tabs-design-obligation-tab-trigger">
          <p className="typo-primary-500">
            <T {...pretailoringPageMessages.singleBuildingBlockTabDesignObligation} />
          </p>
        </TabsTrigger>
      </TabsList>
      <TabsContent
        tabIndex={-1}
        value="general-details"
        className="flex flex-col gap-y-4 data-[state=inactive]:hidden"
        data-testid="building-block-details-tabs-general-details-tab-content"
      >
        <GeneralDetailsTab bb={bb} />
      </TabsContent>
      <TabsContent
        tabIndex={-1}
        value="design-obligation"
        className="flex flex-col gap-y-4 data-[state=inactive]:hidden"
        data-testid="building-block-details-tabs-design-obligation-tab-content"
      >
        <DesignObligationTab bb={bb} />
      </TabsContent>
      <TabsContent
        tabIndex={-1}
        value="data-blocks"
        className="flex flex-col gap-y-4 data-[state=inactive]:hidden"
        data-testid="building-block-details-tabs-data-blocks-tab-content"
      >
        <DataBlocksTab bb={bb} />
      </TabsContent>
      <TabsContent
        tabIndex={-1}
        value="information"
        className="flex flex-col gap-y-4 data-[state=inactive]:hidden"
        data-testid="building-block-details-tabs-information-tab-content"
      >
        <InformationTab bb={bb} />
      </TabsContent>
    </Tabs>
  );
};
